'use client';

import { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { truncateString } from '@/utils/functions/stringFormat';

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export type ContextFile = {
  id: string;
  filename: string;
  original_name: string;
  category: string;
  municipality_code?: string;
  file_size: number;
  created_at: string;
  knowledge_count: number;
  actions: ReactNode;
};

export const contextFilesColumns: ColumnDef<ContextFile>[] = [
  {
    accessorKey: 'original_name',
    header: 'Nome do Arquivo',
    size: 250,
    cell: function DocumentNameCell({ row }: { row: { original: ContextFile } }) {
      return (
        <div className="whitespace-normal break-words w-[180px]">
          {row.original.original_name}
        </div>
      );
    },
  },
  {
    accessorKey: 'filename',
    header: 'Nome do Sistema',
    size: 200,
    cell: function FilenameCell({ row }: { row: { original: ContextFile } }) {
      return (
        <div className="whitespace-normal break-words w-[180px]">
          {truncateString(row.original.filename, 30)}
        </div>
      );
    },
  },
  {
    accessorKey: 'knowledge_count',
    header: 'Conhecimentos',
    size: 120,
    cell: function KnowledgeCountCell({ row }: { row: { original: ContextFile } }) {
      return (
        <div className="whitespace-normal break-words w-[100px]">
          {row.original.knowledge_count}
        </div>
      );
    },
  },
  {
    accessorKey: 'category',
    header: 'Categoria',
    size: 150,
    cell: function CategoryCell({ row }: { row: { original: ContextFile } }) {
      return (
        <div className="whitespace-normal break-words w-[120px]">
          {row.original.category || '-'}
        </div>
      );
    },
  },
  {
    accessorKey: 'municipality_code',
    header: 'Município',
    size: 150,
    cell: function MunicipalityCell({ row }: { row: { original: ContextFile } }) {
      return (
        <div className="whitespace-normal break-words w-[120px]">
          {row.original.municipality_code || '-'}
        </div>
      );
    },
  },
  {
    accessorKey: 'file_size',
    header: 'Tamanho',
    size: 120,
    cell: function FileSizeCell({ row }: { row: { original: ContextFile } }) {
      return (
        <div className="whitespace-normal break-words w-[100px]">
          {formatFileSize(row.original.file_size)}
        </div>
      );
    },
  },
  {
    accessorKey: 'created_at',
    header: 'Data de Upload',
    size: 180,
    cell: function CreatedAtCell({ row }: { row: { original: ContextFile } }) {
      return (
        <div className="whitespace-normal break-words w-[160px]">
          {formatDate(row.original.created_at)}
        </div>
      );
    },
  },
  {
    accessorKey: 'actions',
    header: '',
    size: 60,
    cell: function ActionsCell({ row }: { row: { original: ContextFile } }) {
      return (
        <div
          className="w-full flex justify-end"
          onClick={(e) => e.stopPropagation()}
        >
          {row.original.actions}
        </div>
      );
    },
  },
];
